import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router-dom"
import {
  Shield, AlertTriangle, CheckCircle2, AlertOctagon, ChevronDown,
  ChevronUp, RefreshCw, ExternalLink, Info, Wrench,
} from "lucide-react"

/**
 * /security — Plattform-Sicherheits-Dashboard (Phase 7c+, 2026-05-08).
 *
 * Komplett neu geschrieben am 08.05. abends nach User-Feedback:
 * - Klartext statt Tech-Jargon
 * - Findings mit konkreten IDs + Links zur betroffenen Aufgabe/Mail
 * - System-Status (28 Health-Checks) ist jetzt Teil dieser Seite (nicht mehr extra)
 *
 * Layout:
 * 1. Status-Header — "Alles ok" oder "X Sachen anschauen"
 * 2. Heute (1 Zeile pro KPI: Sends, Kosten, ggf. Alerts)
 * 3. Findings — klappbar mit konkreten Details + Klick-Through
 * 4. Hintergrund-Tests — Synthetic, Backup, Pause-Status
 * 5. Health-Checks im Detail (klappbar) — ehemals /status
 */

// ─── Lokalisations-Mapping ──────────────────────────────────────────────────
// check_name → { titel, erklaerung }. Fallback: snake_case verschönert.

const FINDING_LABELS: Record<string, { title: string; explanation: string }> = {
  orphan_tasks: {
    title: "Aufgaben zeigen auf gelöschte Projekte",
    explanation: "Diese Aufgaben verweisen auf Projekt-IDs, die in HERO nicht mehr existieren. Können vermutlich gelöscht werden.",
  },
  old_drafts: {
    title: "Alte unbeantwortete E-Mails",
    explanation: "E-Mails seit 7+ Tagen im Review ohne Antwort. Entweder antworten oder als 'erledigt' markieren.",
  },
  duplicate_tasks: {
    title: "Doppelte Aufgaben im Cache",
    explanation: "Eine HERO-Task-ID erscheint mehrfach. Sync-Bug — Cache zurücksetzen kann helfen.",
  },
  emails_no_tenant: {
    title: "E-Mails ohne Tenant-Zuordnung",
    explanation: "Cross-Tenant-Risiko: E-Mails sollten immer einem Tenant zugeordnet sein.",
  },
  stuck_pending_sends: {
    title: "Sends hängen seit 5+ Min im Versand",
    explanation: "Vermutlich Service-Crash mitten im SMTP-Call. Watchdog setzt sie nach 5min auf 'failed'.",
  },
  stuck_emails_syncing: {
    title: "E-Mails hängen seit 24h+ im Sync",
    explanation: "IMAP-Sync ist stecken geblieben. IMAP-Verbindung prüfen.",
  },
  send_without_imap: {
    title: "Tenant sendet ohne IMAP-Konfig",
    explanation: "Kritisch: Mails gehen raus, aber kein Eingangs-Tracking möglich.",
  },
  pending_send_retry_loop: {
    title: "Sends mit 3+ Versuchen",
    explanation: "Wiederholte Fehler. Vermutlich SMTP-Problem oder kaputter Empfänger.",
  },
  tz_naive_received_at: {
    title: "E-Mails mit kaputter Zeitzone",
    explanation: "Naive UTC-Strings ohne Offset. UI zeigt 2h Versatz. Backfill +2h kann nötig sein.",
  },
  tenants_no_hero_token: {
    title: "Aktive Tenants ohne HERO-Token",
    explanation: "Sendet/empfängt, kann aber nichts in HERO schreiben.",
  },
  send_audit_orphan_tenant: {
    title: "Sends von unbekannten Tenants",
    explanation: "Send-Audit-Eintrag für Tenant der nicht (mehr) existiert.",
  },
  duplicate_project_blocked: {
    title: "Doppel-Projekt verhindert",
    explanation: "Pre-Create-Check hat erkannt: für diesen Kontakt + Gewerk gibt's schon ein offenes Projekt. Bestehende ID wurde wiederverwendet.",
  },
  imap_mass_delete_blocked: {
    title: "IMAP-Massenlöschung BLOCKIERT",
    explanation: "IMAP wollte mehr als 50 Mails als gelöscht markieren. Wahrscheinlich IMAP-Fehler. Mit ENV IMAP_DELETE_CAP_OVERRIDE=true erzwingbar.",
  },
  // Anomaly-Watcher
  anomaly_mismatch_spike: {
    title: "Häufung von Mismatches",
    explanation: "Mehr als 5% der Mails der letzten Stunde hatten einen Mismatch. Auffällig.",
  },
  anomaly_force_override_rate: {
    title: "Viele Sanity-Checks überstimmt",
    explanation: "User klicken oft 'Trotzdem senden'. Entweder Check zu strikt oder echtes Problem.",
  },
  anomaly_send_spike: {
    title: "Send-Spike eines Tenants",
    explanation: "Ein Tenant sendet plötzlich 5× mehr als normal. Loop-Bug oder Account kompromittiert?",
  },
  anomaly_resend_loop: {
    title: "Re-Send-Loop erkannt",
    explanation: "Gleiche E-Mail wurde 3+ Mal in 10min gesendet.",
  },
  anomaly_failure_rate: {
    title: "Erhöhte Send-Fehlerquote",
    explanation: "Mehr als 10% der Sends scheitern. SMTP-Problem oder Tenant-Konfig kaputt.",
  },
  anomaly_silent_tenant: {
    title: "Tenant 24h+ inaktiv",
    explanation: "Tenant der normalerweise sendet, ist seit 24h still. IMAP-Bug?",
  },
  // Cost
  cost_daily_global: {
    title: "Tageskosten zu hoch",
    explanation: "LLM-Tageskosten haben das Limit überschritten.",
  },
  cost_hourly_global: {
    title: "Stundenkosten zu hoch — Auto-Pause aktiv",
    explanation: "Bedrock-Calls werden 30min lang abgewiesen. Loop-Bug?",
  },
  cost_daily_per_tenant: {
    title: "Tageskosten Tenant-Limit",
    explanation: "Ein Tenant über $10/Tag.",
  },
  cost_spike: {
    title: "Plötzlicher Kosten-Spike",
    explanation: "Aktuelle Stunde 5× höher als der 7-Tage-Schnitt.",
  },
}

function findingLabel(check_name: string): { title: string; explanation: string } {
  if (check_name in FINDING_LABELS) return FINDING_LABELS[check_name]
  return {
    title: check_name.replace(/_/g, " "),
    explanation: "Konsistenz-Auffälligkeit. Detail siehe unten.",
  }
}

function healthLabel(check_name: string): string {
  const map: Record<string, string> = {
    "synthetic_send": "Selbsttest E-Mail",
    "backup_restore_test": "Backup-Test",
    "synthetic_e2e_contact": "Test: Kontakt anlegen",
    "synthetic_e2e_project": "Test: Projekt anlegen",
    "synthetic_e2e_calendar_event": "Test: Termin anlegen",
  }
  return map[check_name] || check_name.replace(/^e2e_|_test$/g, "").replace(/_/g, " ")
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface FindingItem {
  task_id?: number
  email_id?: number
  send_id?: number
  hero_task_id?: number
  duplicate_count?: number
  title?: string
  subject?: string
  sender?: string
  missing_project_id?: number
  tenant_slug?: string
  link?: string
  kind?: string
  attempts?: number
  started_at?: string
  received_at?: string
}

interface FindingDetails {
  items?: FindingItem[]
  count?: number
  total_count?: number      // alle (auch ausserhalb des Dashboard-Filters)
  shown_first?: number
  hint?: string
  auto_fix_action?: string  // z.B. "tz_naive_backfill"
  // Legacy (alte Findings vor Anreicherung)
  Anzahl?: number
  Sample?: string
  title?: string
  Hinweis?: string
}

interface DashboardData {
  ok: boolean
  overall_status: "ok" | "warn" | "error"
  health: {
    summary: { ok: number; warn: number; error: number; skip: number; total: number }
    failures: HealthEntry[]
    all: HealthEntry[]
  }
  send_audit_24h: {
    total: number; mismatches: number; forced: number; errors: number
    recent: SendAuditEntry[]
  }
  findings: {
    open_count: number
    recent: Array<{
      id: number; check_name: string; severity: string
      affected_count: number; details: FindingDetails | string
      created_at: string
      /** Wie oft derselbe Befund in 30 Tagen gemeldet wurde (#25.08.2026). */
      meldungen?: number
      /** Seit wann er ununterbrochen besteht. */
      besteht_seit?: string | null
    }>
  }
  pending_sends: { pending: number; sending: number; failed_24h: number; cancelled_24h: number }
  cost_today: { total_usd: number; top_tenants: Array<{ tenant_slug: string; usd: number }> }
  synthetic_send: { status: string | null; message: string | null; latency_ms: number | null; checked_at: string | null }
  backup_test?: { status: string | null; message: string | null; checked_at: string | null }
  llm_pause?: {
    circuit_breaker_active: boolean
    circuit_breaker_until?: string | null
    cost_pause_active: boolean
    cost_pause_until?: string | null
    throttle_counter?: number
  }
}

interface HealthEntry {
  check_name: string; category: string; status: string
  message: string | null; latency_ms?: number | null; checked_at: string | null
}

interface SendAuditEntry {
  id: number; kind: string; tenant_slug: string
  actual_to: string; actual_subject: string
  mismatch_flag: boolean; forced_override: boolean
  error_message: string | null; created_at: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelTime(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    const d = new Date(iso); const ms = Date.now() - d.getTime()
    if (ms < 60_000) return "gerade eben"
    if (ms < 3600_000) return `vor ${Math.floor(ms / 60_000)} Min`
    if (ms < 86400_000) return `vor ${Math.floor(ms / 3600_000)} Std`
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  } catch { return iso }
}

function parseDetails(d: FindingDetails | string | null | undefined): FindingDetails {
  if (!d) return {}
  if (typeof d === "string") {
    try { return JSON.parse(d) as FindingDetails } catch { return {} }
  }
  return d
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function StatusHeader({ status, openFindings }: { status: string; openFindings: number }) {
  const isOk = status === "ok" && openFindings === 0
  const isWarn = status === "warn" || (status === "ok" && openFindings > 0)
  const bg = isOk ? "#dcfce7" : isWarn ? "#fef3c7" : "#fee2e2"
  const fg = isOk ? "#166534" : isWarn ? "#92400e" : "#991b1b"
  const Icon = isOk ? CheckCircle2 : isWarn ? AlertTriangle : AlertOctagon
  const text = isOk
    ? "Alles ok — keine Aktionen nötig"
    : isWarn
      ? `${openFindings > 0 ? `${openFindings} Hinweise` : "Kleinere Auffälligkeiten"} — gelegentlich anschauen`
      : "Kritisch — bitte anschauen"

  return (
    <div style={{
      background: bg, color: fg, padding: 16, borderRadius: 10,
      display: "flex", alignItems: "center", gap: 12, marginBottom: 24,
      border: `1px solid ${fg}33`,
    }}>
      <Icon className="w-6 h-6" style={{ flexShrink: 0 }} />
      <div style={{ fontSize: 16, fontWeight: 600 }}>{text}</div>
    </div>
  )
}

function MetricLine({ icon, label, value, hint, status }: {
  icon: React.ReactNode; label: string; value: string; hint?: string
  status?: "ok" | "warn" | "error"
}) {
  const statusColors = {
    ok: "#16a34a", warn: "#ca8a04", error: "#dc2626",
  }
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 16px", background: "#fff", borderRadius: 8,
      border: "1px solid #e5e7eb",
    }}>
      <div style={{ color: status ? statusColors[status] : "#475569", flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "#475569" }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: status ? statusColors[status] : "#1a1a2e" }}>
        {value}
      </div>
    </div>
  )
}

// ─── Action-Helper: One-Click-Fixes via API ─────────────────────────────────
async function callFindingAction(action: string, body?: object): Promise<{ ok: boolean; message?: string }> {
  const url = `/api/v1/admin/findings/${action}`
  const r = await fetch(url, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : "{}",
  })
  if (!r.ok) {
    const t = await r.text().catch(() => "")
    throw new Error(`HTTP ${r.status}: ${t.slice(0, 200)}`)
  }
  return r.json()
}

function FindingCard({ finding }: { finding: DashboardData["findings"]["recent"][number] }) {
  const [expanded, setExpanded] = useState(false)
  const [actionRunning, setActionRunning] = useState(false)
  const [actionResult, setActionResult] = useState<string | null>(null)
  const qc = useQueryClient()
  const label = findingLabel(finding.check_name)
  const details = parseDetails(finding.details)
  const items = details.items || []

  const runAction = async (action: string, prompt: string, body?: object) => {
    if (!confirm(prompt)) return
    setActionRunning(true)
    setActionResult(null)
    try {
      const r = await callFindingAction(action, body)
      setActionResult(r.message || (r.ok ? "Erledigt" : "Fehler"))
      // Dashboard refetchen damit das Finding verschwindet/aktualisiert wird
      qc.invalidateQueries({ queryKey: ["security-dashboard"] })
    } catch (e) {
      setActionResult("Fehler: " + (e instanceof Error ? e.message : String(e)))
    } finally {
      setActionRunning(false)
    }
  }

  // Action-Definitionen pro check_name
  const actionsForCheck = (() => {
    if (finding.check_name === "tz_naive_received_at") {
      // Button-Label nutzt total_count (alle naiven, nicht nur letzte 7 Tage
      // wie im Dashboard) — der Backfill korrigiert ja alles. Fallback auf
      // affected_count falls total nicht vorhanden (alte Findings).
      const totalCount = details.total_count ?? finding.affected_count
      const sevenDayCount = finding.affected_count
      const hint = (totalCount > sevenDayCount)
        ? `\n\nHinweis: Im Dashboard sind nur die letzten 7 Tage (${sevenDayCount}) sichtbar — der Backfill fixt aber alle ${totalCount}.`
        : ""
      return [{
        label: `Jetzt korrigieren (+2h für alle ${totalCount})`,
        run: () => runAction(
          "tz-naive-backfill",
          `${totalCount} E-Mails um +2h korrigieren?${hint}\n\nIdempotent — kann beliebig oft laufen, ohne Schaden.`,
        ),
      }]
    }
    if (finding.check_name === "old_drafts") {
      return [
        {
          label: "Älter als 30 Tage erledigen",
          run: () => runAction(
            "old-drafts-bulk-done",
            `Alle E-Mails aelter als 30 Tage auf 'erledigt' setzen?\n\nDie Mails verschwinden aus dem Review. Sie bleiben in der DB, du kannst sie ueber Filter wieder finden.`,
            { min_age_days: 30 },
          ),
        },
        {
          label: "Älter als 90 Tage erledigen (sicherer)",
          run: () => runAction(
            "old-drafts-bulk-done",
            `Alle E-Mails aelter als 90 Tage auf 'erledigt' setzen?`,
            { min_age_days: 90 },
          ),
        },
      ]
    }
    return []
  })()
  const sevColors = {
    error: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
    warn:  { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
    info:  { bg: "#e0f2fe", text: "#075985", border: "#7dd3fc" },
    critical: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  } as const
  const c = sevColors[(finding.severity as keyof typeof sevColors) || "info"] || sevColors.info

  return (
    <div style={{
      background: "#fff", borderRadius: 8,
      border: `1px solid ${c.border}`, marginBottom: 8,
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: "100%", textAlign: "left", padding: "12px 16px",
          background: "transparent", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 12,
        }}
      >
        <div style={{
          padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600,
          background: c.bg, color: c.text, flexShrink: 0,
        }}>
          {finding.severity === "info" ? "Hinweis" : finding.severity === "warn" ? "Achtung" : "Wichtig"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            {label.title}{finding.affected_count > 1 ? ` (${finding.affected_count})` : ""}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {label.explanation}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>
          {/* 🔴 „vor 1 Min" las sich wie „neu" — dabei schreibt der
              Konsistenz-Lauf denselben Befund alle 20 Minuten neu. Auf Prod
              waren das 190 Zeilen fuer EINE Ursache. Jetzt steht da, seit wann
              es ihn gibt; die Uhrzeit der letzten Meldung ist nebensaechlich. */}
          {finding.besteht_seit && (finding.meldungen ?? 1) > 1
            ? `seit ${formatRelTime(finding.besteht_seit)} · ${finding.meldungen}× gemeldet`
            : formatRelTime(finding.created_at)}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div style={{ padding: "0 16px 16px 16px", borderTop: "1px solid #f1f5f9" }}>
          {details.hint && (
            <div style={{ fontSize: 12, color: "#475569", marginTop: 12, marginBottom: 8, fontStyle: "italic" }}>
              💡 {details.hint}
            </div>
          )}
          {/* Action-Buttons (One-Click-Fixes) */}
          {actionsForCheck.length > 0 && (
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, marginBottom: 8,
              padding: 10, background: "#f0f9ff", borderRadius: 6,
              border: "1px solid #bae6fd",
            }}>
              <Wrench className="w-4 h-4" style={{ color: "#0369a1", flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0c4a6e", marginBottom: 6 }}>
                  Schnell-Aktion:
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {actionsForCheck.map((a, idx) => (
                    <button
                      key={idx}
                      onClick={a.run}
                      disabled={actionRunning}
                      style={{
                        padding: "6px 12px", fontSize: 12, fontWeight: 600,
                        background: actionRunning ? "#cbd5e1" : "#0369a1",
                        color: "#fff", border: "none", borderRadius: 6,
                        cursor: actionRunning ? "not-allowed" : "pointer",
                      }}
                    >
                      {actionRunning ? "Läuft…" : a.label}
                    </button>
                  ))}
                </div>
                {actionResult && (
                  <div style={{
                    marginTop: 8, fontSize: 12,
                    color: actionResult.startsWith("Fehler") ? "#dc2626" : "#16a34a",
                    fontWeight: 600,
                  }}>
                    {actionResult.startsWith("Fehler") ? "❌" : "✅"} {actionResult}
                  </div>
                )}
              </div>
            </div>
          )}
          {items.length > 0 ? (
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", fontSize: 13 }}>
              {items.map((it, idx) => (
                <li key={idx} style={{
                  padding: "8px 0", borderBottom: idx < items.length - 1 ? "1px solid #f1f5f9" : "none",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {it.task_id !== undefined && (
                      <span>
                        Aufgabe <strong>#{it.task_id}</strong>
                        {it.title && <span style={{ color: "#475569" }}> — „{it.title}"</span>}
                        {it.missing_project_id && (
                          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                            zeigt auf Projekt #{it.missing_project_id} (nicht in HERO)
                          </div>
                        )}
                      </span>
                    )}
                    {it.email_id !== undefined && (
                      <span>
                        E-Mail <strong>#{it.email_id}</strong>
                        {it.subject && <span style={{ color: "#475569" }}> — „{it.subject}"</span>}
                        {(it.sender || it.received_at) && (
                          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                            {it.sender && <>von {it.sender}</>}
                            {it.received_at && <> · {formatRelTime(it.received_at)}</>}
                          </div>
                        )}
                      </span>
                    )}
                    {it.send_id !== undefined && (
                      <span>
                        Send <strong>#{it.send_id}</strong> ({it.kind})
                        {it.attempts && <span style={{ color: "#94a3b8" }}> · {it.attempts} Versuche</span>}
                      </span>
                    )}
                    {it.hero_task_id !== undefined && (
                      <span>HERO-Aufgabe #{it.hero_task_id} — {it.duplicate_count}× im Cache</span>
                    )}
                    {it.tenant_slug && !it.task_id && !it.email_id && !it.send_id && (
                      <span>Tenant <strong>{it.tenant_slug}</strong></span>
                    )}
                  </div>
                  {it.link && (
                    <Link to={it.link} style={{
                      color: "#1d4ed8", fontSize: 12, fontWeight: 600,
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      Öffnen <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
              Keine Detail-Informationen verfügbar.
              {details.Sample && <div style={{ marginTop: 4 }}>Stichprobe: {details.Sample}</div>}
            </div>
          )}
          {details.count !== undefined && details.shown_first !== undefined && details.count > details.shown_first && (
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
              Zeigt die ersten {details.shown_first} von {details.count}.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function HealthSection({ all, summary }: { all: HealthEntry[]; summary: DashboardData["health"]["summary"] }) {
  const [expanded, setExpanded] = useState(false)

  // Gruppiere nach Kategorie
  const grouped = all.reduce<Record<string, HealthEntry[]>>((acc, h) => {
    const cat = h.category || "andere"
    ;(acc[cat] = acc[cat] || []).push(h)
    return acc
  }, {})

  return (
    <div style={{
      background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb",
      marginTop: 24,
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: "100%", textAlign: "left", padding: "16px 20px",
          background: "transparent", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 12,
        }}
      >
        <Shield className="w-4 h-4" style={{ color: "#475569" }} />
        <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
          Alle Plattform-Tests im Detail ({summary.ok}/{summary.total} ok)
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div style={{ padding: "0 20px 20px 20px", borderTop: "1px solid #f1f5f9" }}>
          {Object.entries(grouped).map(([cat, entries]) => (
            <div key={cat} style={{ marginTop: 16 }}>
              <div style={{
                fontSize: 12, fontWeight: 700, color: "#94a3b8",
                textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8,
              }}>
                {cat}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
                {entries.map(e => (
                  <li key={e.check_name} style={{
                    padding: "6px 0", borderBottom: "1px solid #f8fafc",
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                      background: e.status === "ok" ? "#16a34a" :
                                  e.status === "warn" ? "#ca8a04" :
                                  e.status === "skip" ? "#94a3b8" : "#dc2626",
                    }} />
                    <span style={{ fontWeight: 500, minWidth: 200 }}>{healthLabel(e.check_name)}</span>
                    <span style={{ flex: 1, color: "#64748b", fontSize: 12 }}>
                      {e.message || "—"}
                    </span>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>
                      {e.latency_ms ? `${e.latency_ms}ms · ` : ""}{formatRelTime(e.checked_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function SecurityPage() {
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useQuery<DashboardData>({
    queryKey: ["security-dashboard"],
    queryFn: async () => {
      const r = await fetch("/api/v1/admin/security-dashboard", { credentials: "include" })
      if (!r.ok) {
        const t = await r.text().catch(() => "")
        throw new Error(`HTTP ${r.status}${t ? ` — ${t.slice(0, 300)}` : ""}`)
      }
      return r.json()
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  })

  if (isLoading) return <div style={{ padding: 40, textAlign: "center" }}>Lade…</div>
  // 🔴 Nicht nur „Fehler beim Laden": diese Seite stand ab dem 11.08.2026 neun
  // Tage lang auf genau diesem Satz (HTTP 500 durch Decimal/float), und die
  // Ursache war nur per SSH im uvicorn-Log zu sehen. Was der Server sagt,
  // gehoert auf den Bildschirm.
  if (isError || !data) return (
    <div style={{ padding: 40, color: "#dc2626" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Sicherheits-Übersicht konnte nicht geladen werden.</div>
      <pre style={{
        fontSize: 12, whiteSpace: "pre-wrap", background: "rgba(220,38,38,0.08)",
        padding: 10, borderRadius: 6, marginBottom: 10,
      }}>{error instanceof Error ? error.message : "Unbekannter Fehler"}</pre>
      <button className="pp-btn pp-btn-sm" onClick={() => refetch()}>Erneut versuchen</button>
    </div>
  )

  const cost = data.cost_today
  const llmPaused = data.llm_pause?.cost_pause_active || data.llm_pause?.circuit_breaker_active

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#1a1a2e" }}>
          Sicherheit
        </h1>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>
          Aktualisiert {formatRelTime(new Date(dataUpdatedAt).toISOString())} · auto-refresh 30s
        </div>
      </div>

      {/* Status-Header in Klartext */}
      <StatusHeader status={data.overall_status} openFindings={data.findings.open_count} />

      {/* HEUTE — kompakte Metriken */}
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "#475569", marginBottom: 12, marginTop: 24 }}>
        Heute
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 8 }}>
        <MetricLine
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Gesendete E-Mails (24h)"
          hint={data.send_audit_24h.mismatches > 0 ?
            `⚠️ ${data.send_audit_24h.mismatches} Mismatch geblockt` :
            data.send_audit_24h.forced > 0 ? `${data.send_audit_24h.forced} mit 'Trotzdem senden'` :
            "alle ok"}
          value={String(data.send_audit_24h.total)}
          status={data.send_audit_24h.mismatches > 0 ? "warn" : "ok"}
        />
        <MetricLine
          icon={<span style={{ fontSize: 16, fontWeight: 700 }}>$</span>}
          label="KI-Kosten heute"
          hint={cost.top_tenants.length > 0 ? `Top: ${cost.top_tenants[0].tenant_slug}` : `Limit: $25/Tag`}
          value={`$${cost.total_usd.toFixed(2)}`}
          status={cost.total_usd > 25 ? "error" : cost.total_usd > 10 ? "warn" : "ok"}
        />
        <MetricLine
          icon={<Shield className="w-5 h-5" />}
          label="In der Warteschlange"
          hint={data.pending_sends.failed_24h > 0 ?
            `${data.pending_sends.failed_24h} fehlgeschlagen (24h)` :
            `${data.pending_sends.cancelled_24h} abgebrochen (24h)`}
          value={String(data.pending_sends.pending + data.pending_sends.sending)}
          status={data.pending_sends.failed_24h > 0 ? "error" : "ok"}
        />
      </div>

      {/* FINDINGS — klappbar mit Click-Through */}
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "#475569", marginBottom: 12, marginTop: 32 }}>
        Hinweise zum Aufräumen ({data.findings.open_count})
      </h2>
      {data.findings.recent.length === 0 ? (
        <div style={{ padding: 16, background: "#dcfce7", borderRadius: 8, color: "#166534", fontSize: 13 }}>
          Keine Hinweise — alles sauber.
        </div>
      ) : (
        <div>
          {data.findings.recent.map(f => <FindingCard key={f.id} finding={f} />)}
        </div>
      )}

      {/* HINTERGRUND-TESTS */}
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "#475569", marginBottom: 12, marginTop: 32 }}>
        Im Hintergrund
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <MetricLine
          icon={<RefreshCw className="w-5 h-5" />}
          label="Selbsttest E-Mail (stündlich)"
          hint={data.synthetic_send.checked_at ?
            `Letzter Run ${formatRelTime(data.synthetic_send.checked_at)}` :
            "Noch kein Run — läuft alle 60min"}
          value={data.synthetic_send.status === "ok" ? "OK" : data.synthetic_send.status === "error" ? "FEHLER" : "—"}
          status={data.synthetic_send.status === "ok" ? "ok" : data.synthetic_send.status === "error" ? "error" : undefined}
        />
        <MetricLine
          icon={<Shield className="w-5 h-5" />}
          label="DB-Backup-Test (wöchentlich)"
          hint={data.backup_test?.checked_at ?
            `Letzter Run ${formatRelTime(data.backup_test.checked_at)}` :
            "Läuft 1× pro Woche"}
          value={data.backup_test?.status === "ok" ? "OK" : data.backup_test?.status === "error" ? "FEHLER" : "—"}
          status={data.backup_test?.status === "ok" ? "ok" : data.backup_test?.status === "error" ? "error" : undefined}
        />
        <MetricLine
          icon={<Info className="w-5 h-5" />}
          label="KI-Status (Bedrock)"
          hint={data.llm_pause?.cost_pause_active ?
            "Kosten-Pause aktiv — Calls werden abgewiesen" :
            data.llm_pause?.circuit_breaker_active ?
            "Drosselungs-Schutz aktiv (60s)" :
            "Normal — keine Drosselung"}
          value={llmPaused ? "PAUSE" : "OK"}
          status={llmPaused ? "error" : "ok"}
        />
        <MetricLine
          icon={<Shield className="w-5 h-5" />}
          label="Letzte gesendete E-Mails"
          hint={data.send_audit_24h.recent.length > 0 ?
            `Letzter Send ${formatRelTime(data.send_audit_24h.recent[0].created_at)}` :
            "noch nichts gesendet"}
          value={String(data.send_audit_24h.recent.length)}
          status="ok"
        />
      </div>

      {/* RECENT SENDS */}
      {data.send_audit_24h.recent.length > 0 && (
        <>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "#475569", marginBottom: 12, marginTop: 32 }}>
            Letzte Sends (Forensik)
          </h2>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb" }}>
            {data.send_audit_24h.recent.slice(0, 10).map((s, idx) => {
              const kindLabel = {
                reply: "Antwort", forward: "Weiterleitung",
                calendar_notify: "Termin-Info", compose: "Neue Mail",
              }[s.kind] || s.kind
              const status = s.error_message ? "error" : s.mismatch_flag ? "warn" : "ok"
              return (
                <div key={s.id} style={{
                  padding: "10px 16px",
                  borderBottom: idx < Math.min(9, data.send_audit_24h.recent.length - 1) ? "1px solid #f1f5f9" : "none",
                  display: "flex", alignItems: "center", gap: 12, fontSize: 13,
                }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: status === "ok" ? "#dcfce7" : status === "warn" ? "#fef3c7" : "#fee2e2",
                    color: status === "ok" ? "#166534" : status === "warn" ? "#92400e" : "#991b1b",
                  }}>
                    {kindLabel}
                  </span>
                  <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    → {s.actual_to}
                    {s.actual_subject && <span style={{ color: "#64748b" }}> · „{s.actual_subject}"</span>}
                  </div>
                  {s.error_message && (
                    <span
                      style={{ fontSize: 12, color: "#991b1b", fontWeight: 600, whiteSpace: "nowrap" }}
                      title={s.error_message}
                    >
                      NICHT gesendet
                    </span>
                  )}
                  {s.mismatch_flag && (
                    <span style={{ fontSize: 12, color: s.forced_override ? "#92400e" : "#991b1b" }}>
                      {s.forced_override ? "Mismatch überstimmt" : "blockiert"}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>
                    {formatRelTime(s.created_at)}
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* HEALTH-DETAIL (ehemals /status) */}
      <HealthSection all={data.health.all || []} summary={data.health.summary} />

      {/* FOOTER */}
      <div style={{
        marginTop: 32, padding: 16, background: "#f8fafc", borderRadius: 8,
        fontSize: 12, color: "#64748b",
      }}>
        <strong>Wo gibt's was zu sehen?</strong>
        <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
          <li>Hier: alle Sicherheits-Infos auf einen Blick</li>
          <li>Alarm: Eintrag im Aktivitätslog, bei kritischen Ereignissen zusätzlich Web-Push</li>
          <li>Aktivität-Seite: alle Plattform-Aktionen (wer, was, wann)</li>
        </ul>
      </div>
    </div>
  )
}
